// The Text button and its compose box (CRM-TEXT-2026-10-16). Logic lives in ./lib/staffText; these are the controls,
// kept out of App.jsx so a test can render them with react-dom alone (this repo has no @testing-library).
//
// The compose box: the last three messages of the thread, three one-tap templates Matt can edit (saved to
// public.settings), the text, "Send anyway" for outside 8am-8pm their time, and Send -- which sends ONCE per click.
// Every rule is the Worker's; a refusal is shown in its own words and nothing is retried on its own.
import React, { useEffect, useRef, useState } from 'react';
import {
  SMS_MAX, STAFF_SENDER_LABEL, STAFF_TEXT_ERRORS, createSingleFlightSender, fillTemplate, parseTemplates,
  textButtonState, threadPreview,
} from './lib/staffText';
import { deliveryLine, TONE_COLOR } from './lib/appPolish';

const btn = { borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' };

/** On every lead row, whatever the source. Greyed (reason on hover) only with no textable number or after STOP. */
export function TextButton({ lead, onOpen }) {
  const s = textButtonState(lead);
  return (
    <button className="nb" data-testid="lead-text" disabled={!s.enabled} title={s.reason}
      onClick={() => s.enabled && onOpen(lead)}
      style={{ ...btn, marginLeft: 6, background: s.enabled ? '#0f766e' : '#f1f5f9', color: s.enabled ? 'white' : '#64748b',
               cursor: s.enabled ? 'pointer' : 'not-allowed' }}>
      Text
    </button>
  );
}

function fmt(at) {
  try { return new Date(at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (_) { return ''; }
}

// CRM-LEADS-2026-10-16 §3: after a send the box clears and the sent text joins the thread at once (it had stayed in the box,
// looking unsent). "Hand back to agent" shows while the conversation is in staff hands -- the agents window sets
// human_takeover on the first staff text (AGENT-DEADEND-2026-10-16) -- and clears it through /api/staff-release.
// NIGHT-DIRECTSTAFF-2026-10-22 §6: waiting = this lead's texts queued offline ("Waiting to send"), each with Cancel (onCancelWaiting)
export function TextCompose({ lead, loadThread, loadTemplates, saveTemplates, sendText, onClose, onSent,
  conversationId = null, takeover = false, onHandBack = null, focusBody = false, waiting = [], onCancelWaiting = () => {} }) {
  const [convId, setConvId] = useState(conversationId);
  const [inHands, setInHands] = useState(!!takeover);
  const [handing, setHanding] = useState(false);
  const [thread, setThread] = useState(null);       // null = loading
  // §6 (NIGHT-DIRECTSTAFF-2026-10-19): on a phone the keyboard must never cover Send, and the thread opens at the newest
  const threadRef = useRef(null);
  const sendRef = useRef(null);
  const keepSendInView = () => { setTimeout(() => { if (sendRef.current && sendRef.current.scrollIntoView) sendRef.current.scrollIntoView({ block: 'nearest' }); }, 250); };
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [thread]);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const onResize = () => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;   // the box shrank: keep the newest in view
      if (typeof document !== 'undefined' && document.activeElement && document.activeElement.dataset && document.activeElement.dataset.testid === 'text-body') keepSendInView();
    };
    if (vv) vv.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
    return () => { if (vv) vv.removeEventListener('resize', onResize); window.removeEventListener('resize', onResize); };
  }, []);
  const [templates, setTemplates] = useState(() => parseTemplates(null));
  const [editing, setEditing] = useState(false);
  const [draftTpl, setDraftTpl] = useState(templates);
  const [body, setBody] = useState('');
  const [anyway, setAnyway] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);         // { code, message }
  const [done, setDone] = useState(null);
  const sender = useRef(null);
  if (!sender.current) sender.current = createSingleFlightSender((p) => sendText(p.leadId, p.body, p.sendAnyway));

  // Loaded once per lead: the parent passes fresh closures on every render, so they are read through a ref.
  const loaders = useRef({ loadThread, loadTemplates });
  loaders.current = { loadThread, loadTemplates };
  const leadId = lead.id;
  useEffect(() => {
    let live = true;
    const { loadThread: lt, loadTemplates: ltp } = loaders.current;
    // NIGHT-DIRECTSTAFF-2026-10-19 §6: the last 10, in a box that opens scrolled to the newest
    Promise.resolve(lt ? lt() : []).then((m) => { if (live) setThread(threadPreview(m, 10)); })
      .catch(() => { if (live) setThread([]); });
    Promise.resolve(ltp ? ltp() : null).then((t) => { if (live && t) { setTemplates(t); setDraftTpl(t); } })
      .catch(() => {});
    return () => { live = false; };
  }, [leadId]);

  const send = async () => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true); setError(null);
    let r;
    try {
      r = await sender.current({ leadId: lead.id, body: text, sendAnyway: anyway });
    } catch (e) {
      const code = e && e.code ? e.code : 'unknown';
      setError({ code, message: (e && e.message) || STAFF_TEXT_ERRORS[code] || 'Send failed. Try again.' });
      setBusy(false);
      return;
    }
    // A click that arrived while a send was in flight changes nothing: the first send still owns the button.
    if (r && r.skipped) return;
    // NIGHT-DIRECTSTAFF-2026-10-22 §6: offline, the text is kept on this device and goes once when the connection is back
    if (r && r.queued) { setBusy(false); setBody(''); setDone('Saved: sending when online.'); if (onSent) onSent(r); return; }
    setBusy(false);
    setBody('');                                        // sent: the box is clear, and the text is in the thread below
    if (!(r && r.duplicate)) setThread((t) => [...(t || []), { who: STAFF_SENDER_LABEL, at: new Date().toISOString(), body: text }].slice(-4));
    if (r && r.conversation_id) setConvId(r.conversation_id);
    setInHands(true);                                   // a staff text takes the thread over
    setDone(r && r.duplicate ? 'Already sent a moment ago; not sent twice.' : 'Sent.');
    if (onSent) onSent(r);
  };
  const handBack = async () => {
    if (!onHandBack || !convId || handing) return;
    setHanding(true); setError(null);
    try { await onHandBack(convId); setInHands(false); setDone('Handed back: the agent picks up from here.'); }
    catch (e) { setError({ code: 'release', message: (e && e.message) || 'Could not hand back to the agent.' }); }
    finally { setHanding(false); }
  };

  const saveTpl = async () => {
    const next = parseTemplates(draftTpl);
    try { await saveTemplates(next); setTemplates(next); setEditing(false); }
    catch (e) { setError({ code: 'templates', message: 'Could not save the templates. Nothing else changed.' }); }
  };

  const who = lead.name || lead.email || 'this lead';
  return (
    <div data-testid="text-compose-overlay" role="dialog" aria-modal="true" aria-label={`Text ${who}`}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 50, display: 'flex',
               alignItems: 'flex-start', justifyContent: 'center', padding: 12, boxSizing: 'border-box', overflowY: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div data-testid="text-compose" className="card"
        style={{ width: '100%', maxWidth: 520, boxSizing: 'border-box', padding: 16, background: 'white', borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: 0, overflowWrap: 'anywhere' }}>Text {who}</h3>
          <button className="nb" data-testid="text-close" onClick={onClose} disabled={busy} aria-label="Close"
            style={{ ...btn, background: '#f1f5f9', color: '#475569' }}>Close</button>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>From the business line. Replies come back to this lead's conversation.</div>

        <div data-testid="text-thread" ref={threadRef} tabIndex={0} role="log" aria-label="Recent texts" style={{ marginTop: 12, background: '#f8fafc', borderRadius: 8, padding: 10, fontSize: 12.5, maxHeight: '32vh', overflowY: 'auto' }}>
          {thread === null ? <span style={{ color: '#64748b' }}>Loading the thread…</span>
            : thread.length === 0 ? <span style={{ color: '#64748b' }}>No texts yet. This will be the first.</span>
            : thread.map((m, i) => (
              <div key={i} data-testid="text-thread-line" style={{ marginBottom: i < thread.length - 1 ? 6 : 0, overflowWrap: 'anywhere' }}>
                <strong style={{ color: m.who === 'them' ? '#0f172a' : m.who === STAFF_SENDER_LABEL ? '#b45309' : '#4338ca' }}>{m.who}</strong>
                {m.who === STAFF_SENDER_LABEL
                  // §5 (NIGHT-DIRECTSTAFF-2026-10-17): Sent ✓ 18:39 / Delivered / Failed: the plain reason
                  ? (() => { const dl = deliveryLine({ status: m.status, error_code: m.error_code, error_message: m.error_message, created_at: m.at }); return <span data-testid="text-thread-delivery" style={{ color: TONE_COLOR[dl.tone] }}> · {dl.text}</span>; })()
                  : <>{m.at ? <span style={{ color: '#64748b' }}> · {fmt(m.at)}</span> : null}{m.failed ? <span style={{ color: '#b91c1c' }}> · not delivered</span> : null}</>}
                <div style={{ color: '#334155', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{m.body}</div>
              </div>
            ))}
        </div>

        {waiting.length > 0 && (
          <ul data-testid="text-waiting" style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
            {waiting.map((w) => (
              <li key={w.id} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', background: '#e0f2fe', borderRadius: 8, padding: '6px 10px', marginTop: 4, fontSize: 12.5 }}>
                <span style={{ overflowWrap: 'anywhere' }}><strong>Waiting to send</strong> · {w.payload && w.payload.body}</span>
                <button className="nb" data-testid={`text-waiting-cancel-${w.id}`} onClick={() => onCancelWaiting(w.id)}
                  style={{ ...btn, minHeight: 44, background: '#f1f5f9', color: '#334155', flexShrink: 0 }}>Cancel</button>
              </li>
            ))}
          </ul>
        )}
        {!editing ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            {templates.map((t) => (
              <button key={t.key} className="nb" data-testid="text-template" onClick={() => { setBody(fillTemplate(t.body, lead)); setDone(null); }}
                style={{ ...btn, background: '#e0f2fe', color: '#075985' }}>{t.label}</button>
            ))}
            <button className="nb" data-testid="text-templates-edit" onClick={() => { setDraftTpl(templates); setEditing(true); }}
              style={{ ...btn, background: 'transparent', color: '#1d4ed8' }}>Edit templates</button>
          </div>
        ) : (
          <div data-testid="text-templates-editor" style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{'{first}'} becomes the lead's first name.</div>
            {draftTpl.map((t, i) => (
              <label key={t.key} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 8 }}>
                {t.label}
                <textarea data-testid="text-template-body" value={t.body} maxLength={SMS_MAX} rows={3}
                  onChange={(e) => { const v = e.target.value; setDraftTpl((d) => d.map((x, j) => (j === i ? { ...x, body: v } : x))); }}
                  style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, fontSize: 13, fontFamily: 'inherit', padding: 8, border: '1px solid #cbd5e1', borderRadius: 7 }} />
              </label>
            ))}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="nb" data-testid="text-templates-save" onClick={saveTpl} style={{ ...btn, background: '#1d4ed8', color: 'white' }}>Save templates</button>
              <button className="nb" onClick={() => setEditing(false)} style={{ ...btn, background: '#f1f5f9', color: '#475569' }}>Cancel</button>
            </div>
          </div>
        )}

        <textarea data-testid="text-body" value={body} maxLength={SMS_MAX} rows={5} placeholder="Type a text…" autoFocus={focusBody}
          onFocus={keepSendInView} onChange={(e) => { setBody(e.target.value); setDone(null); }}
          style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 12, fontSize: 14, fontFamily: 'inherit', padding: 10, border: '1px solid #cbd5e1', borderRadius: 8 }} />
        <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>{body.length}/{SMS_MAX}</div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: error && error.code === 'quiet_hours' ? '#92400e' : '#475569', marginTop: 6 }}>
          <input type="checkbox" data-testid="text-send-anyway" checked={anyway} onChange={(e) => setAnyway(e.target.checked)} />
          Send anyway, even outside 8am-8pm their time
        </label>

        {error && <div data-testid="text-error" role="alert" style={{ marginTop: 10, fontSize: 13, color: '#b91c1c', overflowWrap: 'anywhere' }}>{error.message}</div>}
        {done && <div data-testid="text-done" style={{ marginTop: 10, fontSize: 13, color: '#166534' }}>{done}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {inHands && onHandBack && convId && (
            <button className="nb" data-testid="text-hand-back" onClick={handBack} disabled={handing || busy}
              title="Staff have this conversation; hand it back and the agent resumes"
              style={{ ...btn, padding: '9px 18px', fontSize: 13, background: '#92400e', color: 'white' }}>{handing ? 'Handing back…' : 'Hand back to agent'}</button>
          )}
          <button className="nb" data-testid="text-send" ref={sendRef} onClick={send} disabled={busy || !body.trim()}
            style={{ ...btn, padding: '9px 18px', fontSize: 13, background: busy || !body.trim() ? '#94a3b8' : '#0f766e', color: 'white',
                     cursor: busy || !body.trim() ? 'default' : 'pointer' }}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
